import React, { useState } from 'react';
import {
  ArrowLeft,
  Send,
  User,
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  HelpCircle,
  FileText,
  Clock,
  BookOpen,
  Calendar,
  Award
} from 'lucide-react';
import { UserSession } from '../types';

interface MobileAssistantViewProps {
  user: UserSession;
  priorTab?: string;
  onBack?: () => void;
  onNavigateTab: (tab: string) => void;
  onSelectClient?: (clientId: string) => void;
}

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: string;
  sources?: string[];
  suggestedAction?: {
    label: string;
    tab: string;
  };
}

const ASSISTANT_QUICK_PROMPTS = [
  {
    label: 'Goal Progress Strategies',
    icon: Sparkles,
    prompt: 'What are effective documentation strategies for tracking articulation goals during pediatric speech sessions?'
  },
  {
    label: 'Medicaid Billing Rules',
    icon: ShieldCheck,
    prompt: 'Review Arizona Medicaid and DDD rules for SLPA session documentation and 53-minute requirements.'
  },
  {
    label: 'Objective Soap Notes',
    icon: FileText,
    prompt: 'How should I structure the Objective section for a daily SOAP encounter note with multimodal cueing?'
  },
  {
    label: 'Weekly Visit Carryover',
    icon: Calendar,
    prompt: 'Give carryover suggestions for parents to practice speech sound carryover at home between weekly visits.'
  }
];

const SUPERVISOR_QUICK_PROMPTS = [
  {
    label: 'Co-Signature Protocols',
    icon: ShieldCheck,
    prompt: 'What are the AHCCCS and DDD supervisory co-signature requirements and timeline standards for SLPA encounter notes?'
  },
  {
    label: 'Direct Supervision Audit',
    icon: Clock,
    prompt: 'Review Arizona regulatory requirements for SLPA supervisory hours (direct vs indirect observation) and documentation.'
  },
  {
    label: 'Plan of Care Recertification',
    icon: FileText,
    prompt: 'What clinical criteria and standardized metrics should be highlighted during annual speech therapy plan of care re-evaluations?'
  },
  {
    label: 'Supervisory Guidance Notes',
    icon: Award,
    prompt: 'How to structure formative feedback and actionable recommendations in supervisor note reviews?'
  }
];

export const MobileAssistantView: React.FC<MobileAssistantViewProps> = ({
  user,
  priorTab = 'notes',
  onBack,
  onNavigateTab,
  onSelectClient
}) => {
  const isSupervisor = user.role === 'SUPERVISOR';
  const isAssistant = user.role === 'ASSISTANT';
  const roleTheme = isSupervisor ? 'SUPERVISOR' : 'ASSISTANT';
  const activePrompts = isSupervisor ? SUPERVISOR_QUICK_PROMPTS : ASSISTANT_QUICK_PROMPTS;

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      sender: 'ai',
      text: isSupervisor
        ? `Hello ${user.name || 'Supervisor'}. I am your Bloom Clinical AI Supervisory Assistant, specialized in supervisory co-signatures, regulatory compliance (AHCCCS / DDD), and caseload oversight. How can I assist your supervisory workflow today?`
        : `Hello ${user.name || 'Clinician'}. I am your Bloom Clinical Assistant, grounded in your EMR clinical standards and Medicaid documentation guidelines. How can I assist your therapy workflow today?`,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sources: ['Allegiant Clinical Standards', 'AHCCCS / DDD Treatment Guidelines', 'ASHA Supervision Standards']
    }
  ]);
  const [inputPrompt, setInputPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  const formatPriorTabName = (tab: string) => {
    switch (tab) {
      case 'notes':
        return 'Encounter Notes';
      case 'mobile-soap':
        return 'SOAP Home';
      case 'clients':
        return 'Clients';
      case 'clinical':
        return 'Documents';
      case 'assignments':
        return 'Planner';
      case 'my-pay':
        return 'My Pay';
      case 'supervision':
        return 'Supervision Logs';
      default:
        return 'Previous Screen';
    }
  };

  const handleSendMessage = async (customPrompt?: string) => {
    const textToSend = (customPrompt || inputPrompt).trim();
    if (!textToSend || loading) return;

    const userMessage: Message = {
      id: `usr-${Date.now()}`,
      sender: 'user',
      text: textToSend,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputPrompt('');
    setLoading(true);

    try {
      const token = typeof window !== 'undefined'
        ? (localStorage.getItem('allegiant_session_token') || sessionStorage.getItem('allegiant_session_token'))
        : null;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        headers['X-Session-Token'] = token;
      }

      // Attempt backend AI endpoint
      const res = await fetch('/api/gemini/chat', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          prompt: textToSend,
          contextScreen: isSupervisor ? 'supervisor-assistant' : 'mobile-assistant',
          userRole: user.role,
          userName: user.name,
          roleContext: isSupervisor ? 'Supervising Speech-Language Pathologist (SLP)' : 'Speech-Language Pathology Assistant (SLPA)'
        })
      });

      if (res.ok) {
        const data = await res.json();
        const aiMessage: Message = {
          id: `ai-${Date.now()}`,
          sender: 'ai',
          text: data.response || data.message || 'Analysis complete.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sources: data.sources || ['Canonical EMR Schema', 'Medicaid Policy']
        };
        setMessages((prev) => [...prev, aiMessage]);
      } else {
        // Fallback clinical intelligence guidance tailored to clinical role
        const fallbackResponse = generateAssistantGuidance(textToSend);
        const aiMessage: Message = {
          id: `ai-${Date.now()}`,
          sender: 'ai',
          text: fallbackResponse.text,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          sources: fallbackResponse.sources,
          suggestedAction: fallbackResponse.action
        };
        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch {
      const fallbackResponse = generateAssistantGuidance(textToSend);
      const aiMessage: Message = {
        id: `ai-${Date.now()}`,
        sender: 'ai',
        text: fallbackResponse.text,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        sources: fallbackResponse.sources,
        suggestedAction: fallbackResponse.action
      };
      setMessages((prev) => [...prev, aiMessage]);
    } finally {
      setLoading(false);
    }
  };

  const generateAssistantGuidance = (prompt: string): { text: string; sources: string[]; action?: { label: string; tab: string } } => {
    const p = prompt.toLowerCase();

    if (isSupervisor && (p.includes('co-sign') || p.includes('sign') || p.includes('supervis') || p.includes('slpa'))) {
      return {
        text: `Supervisory Co-Signature & Audit Standards (AHCCCS / DDD):
• Review Window: Review and co-sign assistant encounter notes within agency compliance timeline.
• Clinical Audit: Verify that documented minutes reflect direct face-to-face treatment and meet authorized CPT units.
• Supervision Logs: Maintain regular documented supervision hours according to state licensing board rules (direct observation + indirect review).
• Note Feedback: If documentation requires amendment, return with explicit clinical instructions before batch billing clearance.`,
        sources: ['Arizona Department of Health Services SLPA Rules', 'AHCCCS Medical Policy Manual', 'DDD Chapter 7'],
        action: { label: 'Review Notes Queue', tab: 'notes' }
      };
    }

    if (p.includes('soap') || p.includes('objective') || p.includes('narrative')) {
      return {
        text: `For high-standard SOAP documentation as an SLPA:
1. Subjective: Document client arousal level, caregiver attendance, and state upon arrival.
2. Objective: Quantify targeted trials (e.g., 80% accuracy across 30 trials with moderate visual cueing). Specify prompt hierarchy (tactile, visual, phonemic, verbal).
3. Assessment: Compare against previous session baseline. Note whether prompts decreased or response latency improved.
4. Plan: State specific focus for subsequent visit and recommended carryover home practice.`,
        sources: ['Allegiant SLPA Clinical Protocols', 'ASHA Clinical Documentation Guide'],
        action: { label: 'Go to SOAP Home', tab: 'mobile-soap' }
      };
    }

    if (p.includes('medicaid') || p.includes('billing') || p.includes('53') || p.includes('rule')) {
      return {
        text: `Arizona Medicaid (AHCCCS / DDD) key requirements for daily speech services:
• Time Threshold: Direct 1-on-1 contact must meet minimum unit duration (minimum 53 active minutes for full standard unit).
• Co-Signature: Assistant (SLPA) notes require review and supervisor sign-off prior to claim compilation.
• Parental Attestation: Obtain caregiver electronic or documented in-person signature at session completion.
• Authorization: Verify authorization date ranges and available units prior to visit commencement.`,
        sources: ['AHCCCS Medical Policy Manual', 'DDD Provider Manual Ch. 7'],
        action: { label: 'Check Documents', tab: 'clinical' }
      };
    }

    if (p.includes('goal') || p.includes('articulation') || p.includes('cue')) {
      return {
        text: `Clinical Best Practices for Pediatric Goals:
• Multimodal Cueing: Systematically document transitions from maximal (hand-over-hand or tactile placement) to moderate (gestural, phonetic placement mirror) to minimal (independent self-correction).
• Generalization: Target carrier phrases ("I see a ___", "Put on the ___") before advancing to conversational speech.
• Real-time Tracking: Log trials per activity set so objective accuracy reflects genuine performance.`,
        sources: ['ASHA Practice Portal', 'Allegiant Clinical Competency Framework']
      };
    }

    return {
      text: `Bloom Clinical AI is grounded in Allegiant EMR protocols. For direct clinical actions:
• Use Encounter Notes / SOAP Home to review or document sessions.
• Use Clients to inspect current authorizations, POC periods, and guardian info.
• Use Supervision / Documents to verify co-signature queues and compliance audits.`,
      sources: ['Allegiant EMR Systems Manual'],
      action: isSupervisor ? { label: 'Go to Notes Queue', tab: 'notes' } : { label: 'Return to SOAP Home', tab: 'mobile-soap' }
    };
  };

  return (
    <div className={`flex flex-col h-[calc(100vh-8.5rem)] md:h-[calc(100vh-10rem)] max-w-3xl mx-auto select-none ${isSupervisor ? 'bg-[#f1f5f9]' : ''}`}>
      {/* Top Mobile Bar with Back to Working Area */}
      <div className="shrink-0 mb-3">
        <button
          type="button"
          onClick={() => {
            if (onBack) {
              onBack();
            } else {
              onNavigateTab(priorTab);
            }
          }}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border font-medium text-xs shadow-xs transition-all active:scale-[0.99] cursor-pointer ${
            isAssistant
              ? 'bg-white hover:bg-slate-50/90 border-slate-200/90 text-slate-700 hover:text-slate-900'
              : isSupervisor
              ? 'bg-white hover:bg-slate-50 border-slate-200/90 text-slate-700 hover:text-slate-900'
              : 'bg-gradient-to-r from-slate-950 via-sky-950/40 to-slate-950 border-sky-500/30 text-sky-200 hover:text-white'
          }`}
        >
          <div className="flex items-center space-x-2">
            <ArrowLeft className={`w-4 h-4 ${isAssistant ? 'text-cyan-600' : isSupervisor ? 'text-slate-600' : 'text-sky-400'}`} />
            <span>Back to {formatPriorTabName(priorTab)}</span>
          </div>
          <span className={`text-[10px] font-mono ${isAssistant || isSupervisor ? 'text-slate-500' : 'text-slate-400'}`}>Preserved Context</span>
        </button>
      </div>

      {/* Main Assistant Header Card */}
      <div className={`shrink-0 border rounded-2xl p-4 shadow-xs mb-3 ${
        isAssistant
          ? 'bg-white border-slate-200/90'
          : isSupervisor
          ? 'mobile-header-shell border-slate-700/80'
          : 'bg-gradient-to-b from-[#182438] via-[#121c2d] to-[#0d1522] border-slate-700/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)]'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
              isAssistant
                ? 'bg-slate-50 border-slate-200 text-slate-700 shadow-none'
                : isSupervisor
                ? 'bg-white border-slate-200 shadow-xs'
                : 'bg-slate-900 border-slate-700/80 shadow-none'
            }`}>
              <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-6 h-6 shrink-0 object-contain select-none" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h1 className={`text-sm font-bold tracking-tight ${isAssistant ? 'text-slate-900' : 'text-white'}`}>
                  {isSupervisor ? 'Supervisor Clinical AI' : 'Bloom Assistant'}
                </h1>
                <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border ${
                  isAssistant
                    ? 'bg-cyan-50 text-cyan-700 border-cyan-200/80'
                    : isSupervisor
                    ? 'bg-white/90 text-slate-700 border-white/80'
                    : 'bg-cyan-500/15 text-cyan-300 border-cyan-400/30'
                }`}>
                  {isSupervisor ? 'Supervisor Ready' : 'SLPA Ready'}
                </span>
              </div>
              <p className={`text-[11px] mt-0.5 ${isAssistant ? 'text-slate-500' : isSupervisor ? 'text-slate-300' : 'text-slate-400'}`}>
                Connected Clinical AI • Grounded in Allegiant Therapy Standards
              </p>
            </div>
          </div>
        </div>

        {/* Quick Suggestion Chips */}
        <div className="flex items-center gap-1.5 overflow-x-auto pt-3 pb-1 no-scrollbar text-xs">
          {activePrompts.map((chip, idx) => {
            const Icon = chip.icon;
            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleSendMessage(chip.prompt)}
                disabled={loading}
                className={`shrink-0 px-2.5 py-1.5 rounded-lg border text-[11px] transition-all flex items-center space-x-1.5 cursor-pointer disabled:opacity-50 ${
                  isAssistant
                    ? 'bg-slate-100/90 hover:bg-slate-200/80 border-slate-200/90 text-slate-700 hover:text-slate-900 shadow-2xs'
                    : isSupervisor
                    ? 'bg-white hover:bg-slate-50 border-slate-200 text-slate-700 hover:text-slate-900 shadow-xs'
                    : 'bg-slate-800/80 hover:bg-slate-700 border-slate-700/80 hover:border-cyan-500/40 text-slate-200 hover:text-white'
                }`}
              >
                <Icon className={`w-3 h-3 shrink-0 ${isAssistant ? 'text-cyan-600' : isSupervisor ? 'text-slate-600' : 'text-cyan-400'}`} />
                <span className="truncate max-w-[200px]">{chip.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Messages Conversation Stream */}
      <div className="flex-1 overflow-y-auto space-y-3 px-1 pr-1.5">
        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex flex-col ${m.sender === 'user' ? 'items-end' : 'items-start'}`}
          >
            <div
              className={`max-w-[90%] sm:max-w-[80%] rounded-2xl p-3.5 shadow-xs ${
                m.sender === 'user'
                  ? isAssistant
                    ? 'bg-cyan-600 text-white rounded-br-none border border-cyan-700'
                    : isSupervisor
                    ? 'bg-slate-800 text-white rounded-br-none border border-slate-700'
                    : 'bg-gradient-to-r from-sky-600 to-blue-700 text-white rounded-br-none border border-sky-400/30'
                  : isAssistant || isSupervisor
                  ? 'bg-white border border-slate-200/90 text-slate-800 rounded-bl-none shadow-xs'
                  : 'bg-gradient-to-b from-[#182337] via-[#131d2f] to-[#0e1625] border border-slate-700/80 text-slate-200 rounded-bl-none shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)]'
              }`}
            >
              <div className="text-xs leading-relaxed whitespace-pre-wrap">{m.text}</div>

              {/* Sources */}
              {m.sources && m.sources.length > 0 && (
                <div className={`mt-2.5 pt-2 flex items-center gap-1.5 flex-wrap ${
                  isAssistant || isSupervisor ? 'border-t border-slate-200/80' : 'border-t border-slate-700/60'
                }`}>
                  <span className={`text-[9px] uppercase tracking-wider font-bold ${
                    isAssistant || isSupervisor ? 'text-slate-500' : 'text-slate-400'
                  }`}>Sources:</span>
                  {m.sources.map((src, i) => (
                    <span
                      key={i}
                      className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${
                        isAssistant
                          ? 'bg-slate-50 text-cyan-800 border-cyan-200/80'
                          : isSupervisor
                          ? 'bg-slate-50 text-slate-700 border-slate-200'
                          : 'bg-slate-900/90 text-cyan-300 border border-cyan-500/20'
                      }`}
                    >
                      {src}
                    </span>
                  ))}
                </div>
              )}

              {/* Action shortcut if suggested */}
              {m.suggestedAction && (
                <div className={`mt-2.5 pt-2 ${isAssistant || isSupervisor ? 'border-t border-slate-200/80' : 'border-t border-slate-700/60'}`}>
                  <button
                    type="button"
                    onClick={() => onNavigateTab(m.suggestedAction!.tab)}
                    className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-colors ${
                      isAssistant
                        ? 'bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-300'
                        : isSupervisor
                        ? 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'
                        : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40'
                    }`}
                  >
                    <span>{m.suggestedAction.label}</span>
                    <ArrowLeft className="w-3 h-3 rotate-180" />
                  </button>
                </div>
              )}

              <div className={`mt-1 text-[9px] text-right font-mono ${
                isAssistant
                  ? 'text-slate-500'
                  : isSupervisor
                  ? (m.sender === 'user' ? 'text-slate-300' : 'text-slate-500')
                  : 'opacity-60'
              }`}>
                {m.timestamp}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className={`flex items-center space-x-2 p-3 rounded-2xl text-xs w-fit ${
            isAssistant || isSupervisor
              ? 'bg-white border border-slate-200 text-slate-600 shadow-xs'
              : 'bg-slate-900/80 border border-slate-800 text-slate-400'
          }`}>
            <div className={`w-3 h-3 rounded-full border-2 border-t-transparent animate-spin ${
              isAssistant ? 'border-cyan-600' : isSupervisor ? 'border-slate-600' : 'border-cyan-400'
            }`} />
            <span>Consulting clinical intelligence...</span>
          </div>
        )}
      </div>

      {/* Fixed Bottom Input Bar */}
      <div className="shrink-0 pt-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="relative flex items-center"
        >
          <input
            type="text"
            value={inputPrompt}
            onChange={(e) => setInputPrompt(e.target.value)}
            placeholder="Ask Bloom Assistant..."
            disabled={loading}
            className={`w-full rounded-xl pl-4 pr-12 py-3 text-xs placeholder-slate-400 focus:outline-none shadow-xs ${
              isAssistant
                ? 'bg-white border border-slate-300 text-slate-900 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30'
                : isSupervisor
                ? 'bg-white border border-slate-300 text-slate-900 placeholder-slate-500 focus:border-slate-500 focus:ring-1 focus:ring-slate-400/25'
                : 'bg-[#101826] border border-slate-700/80 text-white focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 shadow-inner'
            }`}
          />
          <button
            type="submit"
            disabled={!inputPrompt.trim() || loading}
            className={`absolute right-1.5 p-2 rounded-lg text-white disabled:opacity-40 transition-all active:scale-95 ${
              isAssistant
                ? 'bg-cyan-600 hover:bg-cyan-700 shadow-xs'
                : isSupervisor
                ? 'bg-slate-800 hover:bg-slate-900 shadow-xs'
                : 'bg-gradient-to-r from-cyan-500 to-sky-600 shadow-md shadow-cyan-600/20 hover:brightness-110'
            }`}
            title="Send Message"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </form>
      </div>
    </div>
  );
};
